// The details sheet: what a photo, video or recording says about itself, beside it in the preview.
//
// It slides in beside the stage rather than over it, so the picture gets smaller instead of hidden,
// and it stays open as the preview steps from file to file, which is how people compare shots. It
// leads with the facts the file's kind is read by (see "describe"), then where and when, then the
// rest in groups. On a phone there is no room beside the picture, so there it lies over it.
//
// Where a photo was taken is the one fact about a person rather than a picture, and is treated so.
// The map is drawn here from the coordinates alone, with no tiles fetched, since asking a map
// service for the area around a point tells it where the person was. Taking the coordinates to a
// map service is a step of its own, which names the service it goes to.
//
// The details are read on the API's side when a file is secured, or first opened if it was secured
// before they were, and kept sealed with the person's account key. So a file just opened for the
// first time may have none until its copy is ready, when they are asked for again (see
// "useDownloads").
import classnames from 'classnames'
import PropTypes from 'prop-types'

import Button from '@mui/material/Button'
import ClockIcon from '@mui/icons-material/ScheduleRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import IconButton from '@mui/material/IconButton'
import LockIcon from '@mui/icons-material/LockOutlined'
import PlaceIcon from '@mui/icons-material/PlaceOutlined'

import {describe} from '../../lib/detailsTools'
import {formatClock} from './MediaPlayer'

import './detailsSheet.scss'

// A number from the coordinates, so the same place always draws the same way.
const seedOf = text => [...text].reduce((seed, character) => (seed * 31 + character.charCodeAt(0)) % 999983, 7)

const RINGS = [0, 1, 2, 3]

export const LocalMap = ({place}) => {
  const seed = seedOf(place.coordinates)
  const pinX = 150 + (seed % 40) - 20
  const pinY = 58 + ((seed >> 3) % 16) - 8
  const tilt = (seed % 50) - 25
  const pattern = `details-grid-${seed}`

  return (
    <svg className='details-map' viewBox='0 0 300 116' role='img' aria-label={`A map drawn here around ${place.coordinates}`}>
      <defs>
        <pattern id={pattern} width='20' height='20' patternUnits='userSpaceOnUse'>
          <path d='M20 0H0V20' fill='none' stroke='currentColor' strokeOpacity='.1' strokeWidth='.8' />
        </pattern>
      </defs>
      <rect width='300' height='116' fill={`url(#${pattern})`} />
      <path
        d={`M0 ${70 + (seed % 28)}C90 ${46 + (seed % 22)} 190 ${96 - (seed % 26)} 300 ${54 + (seed % 30)}`}
        fill='none'
        stroke='currentColor'
        strokeOpacity='.24'
        strokeWidth='3'
        strokeLinecap='round'
      />
      {RINGS.map(ring => (
        <ellipse
          key={ring}
          cx={pinX}
          cy={pinY}
          rx={30 + ring * 26 + (seed % 9)}
          ry={18 + ring * 14 + ((seed >> 4) % 7)}
          transform={`rotate(${tilt} ${pinX} ${pinY})`}
          fill='none'
          stroke='currentColor'
          strokeOpacity={0.34 - ring * 0.07}
        />
      ))}
      <circle cx={pinX} cy={pinY} r='15' fill='currentColor' opacity='.16' />
      <circle className='map-pin' cx={pinX} cy={pinY} r='5.5' fill='currentColor' strokeWidth='2' />
    </svg>
  )
}

const placeShape = PropTypes.shape({
  name: PropTypes.string,
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  coordinates: PropTypes.string.isRequired,
  altitude: PropTypes.string
})

LocalMap.propTypes = {place: placeShape.isRequired}

const mapUrl = ({latitude, longitude}) =>
  `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`

const WhereAndWhen = ({taken, place, onNotice}) => {
  const copy = () => navigator.clipboard.writeText(place.coordinates)
    .then(() => onNotice('Coordinates copied'))
    .catch(() => onNotice('The coordinates couldn’t be copied'))

  return (
    <section className='details-group'>
      <h3 className='group-title'>Where and when</h3>
      {taken && <div className='details-taken'><ClockIcon className='details-glyph' />{taken.label} {taken.at}</div>}
      {place && (
        <>
          <LocalMap place={place} />
          <p className='map-note'>Drawn here from the coordinates. No map service was told where you were.</p>
          <div className='place-name'><PlaceIcon className='details-glyph' />{place.name || place.coordinates}</div>
          <div className='place-coordinates'>
            {place.name && `${place.coordinates} · `}{place.altitude && `${place.altitude} up`}
          </div>
          <div className='place-actions'>
            <Button className='details-action' variant='outlined' size='small' onClick={copy}>Copy coordinates</Button>
            <Button
              className='details-action'
              variant='outlined'
              size='small'
              href={mapUrl(place)}
              target='_blank'
              rel='noreferrer'
              title='Sends these coordinates to OpenStreetMap'>
              Open in OpenStreetMap
            </Button>
          </div>
        </>
      )}
      {!place && <div className='details-none'><PlaceIcon className='details-glyph' />No location in this file.</div>}
    </section>
  )
}

WhereAndWhen.propTypes = {
  taken: PropTypes.shape({label: PropTypes.string.isRequired, at: PropTypes.string.isRequired}),
  place: placeShape,
  onNotice: PropTypes.func.isRequired
}

const Group = ({title, rows}) => (
  <section className='details-group'>
    <h3 className='group-title'>{title}</h3>
    <dl className='details-rows'>
      {rows.map(({label, value, mono}) => (
        <div className='details-row' key={label}>
          <dt>{label}</dt>
          <dd className={classnames({mono})}>{value}</dd>
        </div>
      ))}
    </dl>
  </section>
)

Group.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string.isRequired, value: PropTypes.string.isRequired, mono: PropTypes.bool})).isRequired
}

const runtimeOf = file => (file.thumbnail?.durationMs ? formatClock(file.thumbnail.durationMs / 1000) : null)

const DetailsSheet = ({file, kind, details, status, onClose, onNotice}) => {
  const {lead, taken, place, groups} = describe(file, kind, details, runtimeOf(file))

  return (
    <aside className='details-sheet' aria-label={`Details of ${file.filename}`}>
      <header className='details-head'>
        <h2 className='details-title'>Details</h2>
        <IconButton className='details-close' aria-label='Close details' title='Close details (I)' onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </header>

      {status === 'loading' && !details && <p className='details-status' role='status'>Reading details…</p>}
      {status === 'failed' && <p className='details-status'>The details couldn’t be read just now.</p>}

      {lead.length > 0 && (
        <div className='details-lead'>
          {lead.map(({label, value}) => (
            <span className='lead-tile' key={label}>
              <span className='lead-value'>{value}</span>
              <span className='lead-label'>{label}</span>
            </span>
          ))}
        </div>
      )}

      {status === 'ready' && <WhereAndWhen taken={taken} place={place} onNotice={onNotice} />}

      {groups.map(group => <Group key={group.title} {...group} />)}

      <footer className='details-foot'>
        <LockIcon className='details-glyph' />
        Read from the file when it was secured, and kept sealed with your account key.
      </footer>
    </aside>
  )
}

DetailsSheet.propTypes = {
  file: PropTypes.shape({
    filename: PropTypes.string.isRequired,
    filesize: PropTypes.number,
    updatedAt: PropTypes.string.isRequired,
    thumbnail: PropTypes.shape({durationMs: PropTypes.number})
  }).isRequired,
  kind: PropTypes.oneOf(['image', 'video', 'audio']).isRequired,
  // As the API sent them, or null when the file has none or they haven't come yet.
  details: PropTypes.object,
  status: PropTypes.oneOf(['loading', 'ready', 'failed']).isRequired,
  onClose: PropTypes.func.isRequired,
  onNotice: PropTypes.func.isRequired
}

export default DetailsSheet
